export const MESES = [
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
];

export const getCodigoSection = (codigo) => {
  const m = String(codigo || "").match(/^A\.(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

/** Ep.6 c) Provisiones de personal — excluye A.07.3 (provisiones por operaciones comerciales). */
export const isTituloProvisionesPersonal = (titulo, codigoMin) => {
  const lower = String(titulo || "").toLowerCase();
  if (
    lower.includes("operaciones comerciales") ||
    lower.includes("por operaciones comerciales")
  ) {
    return false;
  }
  const section = getCodigoSection(codigoMin);
  if (section != null && section !== 6) return false;
  if (section === 6 && lower.includes("provisiones")) return true;
  return (
    (String(titulo).includes("17 c)") || String(titulo).includes("16 c)")) &&
    lower.includes("provisiones")
  );
};

const isTituloSueldosPersonal = (titulo, codigoMin) => {
  const section = getCodigoSection(codigoMin);
  if (section != null && section !== 6) return false;
  return (
    String(titulo).includes("Sueldos, salarios") ||
    (String(titulo).includes("16") && String(titulo).toLowerCase().includes("sueldos"))
  );
};

const isTituloCargasPersonal = (titulo, codigoMin) => {
  const section = getCodigoSection(codigoMin);
  if (section != null && section !== 6) return false;
  return (
    String(titulo).includes("Cargas sociales") ||
    (String(titulo).includes("17 b)") &&
      String(titulo).toLowerCase().includes("cargas"))
  );
};

export const emptyMarcaKpi = () => ({
  venta: 0,
  coste: 0,
  proyeccionVentas: 0,
  proyeccionCompras: 0,
  pedidosVenta: 0,
  pedidosCompra: 0,
  facturasBorradorVenta: 0,
  resultado: 0,
});

const normalizeForMarcaMatch = (text) =>
  String(text || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

/** Misma lógica que el backend: LIKE %marca% sobre nombre cliente/proveedor */
export const matchMarcaFromText = (text, marcas) => {
  const hay = normalizeForMarcaMatch(text);
  if (!hay || !marcas?.length) return null;
  const sorted = [...marcas].sort((a, b) => b.length - a.length);
  for (const marca of sorted) {
    const needle = normalizeForMarcaMatch(marca);
    if (needle && hay.includes(needle)) return marca;
  }
  return null;
};

const valorDeFilaSerie = (d) => {
  const raw = d?.valor ?? d?.VALOR ?? d?.Base ?? d?.BASE ?? d?.base;
  if (raw === undefined || raw === null) return 0;
  if (typeof raw === "string") {
    return parseFloat(String(raw).replace(",", ".")) || 0;
  }
  return Number(raw) || 0;
};

/** Saldos NPGC: A.01 venta, A.04 coste */
export const aggregateMarcasKpiSaldos = (rawRows) => {
  const byMarca = {};

  (rawRows || []).forEach((r) => {
    const marca = String(r.Nivel2Analitica ?? r.nivel2Analitica ?? "").trim();
    if (!marca) return;

    const cuenta6 = String(r.Cuenta6 ?? r.cuenta6 ?? r.CUENTA ?? r.cuenta ?? "").trim();
    if (!cuenta6) return;

    const section = getCodigoSection(r.CODIGO ?? r.codigo);
    const saldo = Number(r.Saldo ?? r.saldo ?? 0) || 0;
    if (section !== 1 && section !== 4) return;

    if (!byMarca[marca]) {
      byMarca[marca] = emptyMarcaKpi();
    }
    if (section === 1) byMarca[marca].venta += saldo;
    if (section === 4) byMarca[marca].coste += saldo;
  });

  return byMarca;
};

export const formatearImporte = (valor) =>
  (valor ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

export const PYG_TITULO_VENTA_NPGC = "1. Importe neto de la cifra de negocios.";

/** % del importe respecto al total de venta (cifra de negocios). */
export const formatearPctSobreVenta = (valor, ventaTotal) => {
  const venta = Number(ventaTotal) || 0;
  if (venta === 0) return null;
  const pct = (Number(valor) / venta) * 100;
  return `${pct.toLocaleString("es-ES", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })} %`;
};

const isExplotacionSection = (section) =>
  section != null && section >= 1 && section <= 13;

const isFinancieroSection = (section) =>
  section != null && section >= 14 && section <= 19;

const flattenAccountList = (grp) => {
  const list = [];
  (grp.subGroupOrder || []).forEach((sk) => {
    const sub = grp.subGroups[sk];
    if (sub?.accountList) list.push(...sub.accountList);
  });
  return list.sort((a, b) =>
    (a.codigo || "").localeCompare(b.codigo || "", undefined, { numeric: true }),
  );
};

const NPGC_VIRTUAL_PARENTS = [
  { titulo: "1. Importe neto de la cifra de negocios.", codigoMin: "A.01", section: 1 },
  { titulo: "4. Aprovisionamientos.", codigoMin: "A.04", section: 4 },
  { titulo: "5. Otros ingresos de explotación.", codigoMin: "A.05", section: 5 },
  { titulo: "7. Otros gastos de explotación.", codigoMin: "A.07", section: 7 },
  {
    titulo: "11. Deterioro y resultado por enajenaciones del inmovilizado.",
    codigoMin: "A.11",
    section: 11,
  },
  { titulo: "15. Gastos financieros.", codigoMin: "A.15", section: 15 },
];

const CALCULATED_LINES = [
  { key: "A.1) RESULTADO DE EXPLOTACIÓN", kind: "explotacion" },
  { key: "A.2) RESULTADO FINANCIERO", kind: "financiero" },
  {
    key: "A.3) RESULTADO ANTES DE IMPUESTOS",
    formulaLines: [
      { line: "A.1) RESULTADO DE EXPLOTACIÓN", sign: 1 },
      { line: "A.2) RESULTADO FINANCIERO", sign: 1 },
    ],
  },
  {
    key: "A.4) RESULTADO DEL EJERCICIO PROCEDENTE DE OPERACIONES",
    formulaLines: [{ line: "A.3) RESULTADO ANTES DE IMPUESTOS", sign: 1 }],
  },
  {
    key: "A.5) RESULTADO DEL EJERCICIO",
    formulaLines: [
      {
        line: "A.4) RESULTADO DEL EJERCICIO PROCEDENTE DE OPERACIONES",
        sign: 1,
      },
    ],
  },
];

const EXTRA_BLOCK_TITLES = [
  "PROYECCIÓN VENTAS",
  "PROYECCIÓN COMPRAS",
  "PEDIDOS VENTA",
  "PEDIDOS COMPRA",
  "FACTURAS BORRADOR VENTA",
];

const sumGruposPorSeccion = (order, map, predicate) => {
  const mes = {};
  let total = 0;
  for (let m = 1; m <= 12; m++) mes[m] = 0;
  order.forEach((k) => {
    const g = map[k];
    if (!g || g._calculated) return;
    const section = getCodigoSection(g.codigoMin);
    if (!predicate(section)) return;
    for (let m = 1; m <= 12; m++) {
      mes[m] = (mes[m] || 0) + (g.mes[m] || 0);
    }
    total += g.total || 0;
  });
  return { mes, total };
};

const applyVirtualParent = (order, map, { titulo, codigoMin, section }) => {
  const childKeys = order.filter((k) => {
    const g = map[k];
    if (!g || g._virtualParent || g._calculated) return false;
    return getCodigoSection(g.codigoMin) === section;
  });
  if (childKeys.length === 0) return;

  const subGroupOrder = [];
  const subGroups = {};
  const mes = {};
  let total = 0;
  for (let m = 1; m <= 12; m++) mes[m] = 0;

  childKeys.forEach((k) => {
    const g = map[k];
    subGroupOrder.push(k);
    subGroups[k] = {
      title: g.titulo,
      accountList: flattenAccountList(g),
      mes: { ...g.mes },
      total: g.total,
    };
    for (let m = 1; m <= 12; m++) {
      mes[m] = (mes[m] || 0) + (g.mes[m] || 0);
    }
    total += g.total || 0;
  });

  map[titulo] = {
    titulo,
    codigoMin,
    mes,
    total,
    subGroupOrder,
    subGroups,
    _virtualParent: true,
  };

  const minPos = Math.min(...childKeys.map((k) => order.indexOf(k)));
  const newOrder = order.filter((k) => !childKeys.includes(k));
  const spliceAt = newOrder.filter((k) => order.indexOf(k) < minPos).length;
  newOrder.splice(spliceAt, 0, titulo);
  order.length = 0;
  order.push(...newOrder);
};

const mesDeFilaSerie = (d) =>
  Number(
    d?.mes ??
      d?.MES ??
      d?.MES_ENTREGA ??
      d?.MES_COBRO ??
      d?.MexCEX ??
      d?.MesCEX ??
      NaN,
  );

export const calcularProyeccionPorMes = (datos, yearFilter = null) => {
  const yearNum = yearFilter != null ? Number(yearFilter) : null;
  const resultado = {};
  let total = 0;

  MESES.forEach((mes) => {
    const mesNum = parseInt(mes, 10);
    const suma = (datos || [])
      .filter((d) => {
        if (yearNum != null) {
          const rowYear = Number(
            d?.year ?? d?.YEAR ?? d?.YEAR_ENTREGA ?? d?.YearCEX ?? 0,
          );
          if (rowYear && rowYear !== yearNum) return false;
        }
        return mesDeFilaSerie(d) === mesNum;
      })
      .reduce((acc, d) => acc + valorDeFilaSerie(d), 0);

    resultado[mesNum] = suma;
    total += suma;
  });

  resultado.total = total;
  return resultado;
};

export const buildPygAnaliticoStructure = (
  rawData,
  {
    resultadoProyeccionVentas,
    resultadoProyeccionCompras,
    resultadoPedidosVenta,
    resultadoPedidosCompra,
    resultadoFacturasBorradorVenta,
  },
) => {
  const order = [];
  const map = {};
  (rawData || []).forEach((r) => {
    const titulo = (r.Titulo ?? r.titulo ?? "").toString().trim();
    const descGrupo2 = (r.DescGrupo2 ?? r.descGrupo2 ?? "").toString().trim();
    const codigoNpgc = String(r.CODIGO ?? r.codigo ?? "").trim();
    const cuenta6 = String(
      r.Cuenta6 ?? r.cuenta6 ?? r.CUENTA ?? r.cuenta ?? "",
    ).trim();
    const desc = (r.DescripcionCuenta ?? r.descripcionCuenta ?? cuenta6)
      .toString()
      .trim();
    const mesVal = r.Mes ?? r.mes;
    const saldo = Number(r.Saldo ?? r.saldo ?? 0) || 0;
    if (!titulo) return;

    if (!map[titulo]) {
      map[titulo] = {
        titulo,
        codigoMin: codigoNpgc || null,
        mes: {},
        total: 0,
        subGroupOrder: [],
        subGroups: {},
      };
      order.push(titulo);
    }
    const g = map[titulo];
    if (
      codigoNpgc &&
      (!g.codigoMin || codigoNpgc.localeCompare(g.codigoMin) < 0)
    ) {
      g.codigoMin = codigoNpgc;
    }

    const subKey = descGrupo2 || "__root__";
    if (!g.subGroups[subKey]) {
      g.subGroups[subKey] = {
        title: descGrupo2 || null,
        accounts: {},
        mes: {},
        total: 0,
      };
      g.subGroupOrder.push(subKey);
    }
    const sub = g.subGroups[subKey];

    if (cuenta6) {
      if (!sub.accounts[cuenta6]) {
        sub.accounts[cuenta6] = {
          codigo: cuenta6,
          titulo: desc,
          mes: {},
          total: 0,
        };
      }
      const acc = sub.accounts[cuenta6];
      const m = mesVal != null ? Number(mesVal) : null;
      if (m >= 1 && m <= 12) {
        acc.mes[m] = (acc.mes[m] || 0) + saldo;
        sub.mes[m] = (sub.mes[m] || 0) + saldo;
        g.mes[m] = (g.mes[m] || 0) + saldo;
      }
      acc.total += saldo;
      sub.total += saldo;
      g.total += saldo;
    }
  });

  order.forEach((tit) => {
    const g = map[tit];
    g.subGroupOrder.forEach((subKey) => {
      const sub = g.subGroups[subKey];
      sub.accountList = Object.values(sub.accounts).sort((a, b) =>
        (a.codigo || "").localeCompare(b.codigo || "", undefined, {
          numeric: true,
        }),
      );
    });
  });

  const keySueldos = order.find((t) =>
    isTituloSueldosPersonal(t, map[t]?.codigoMin),
  );
  const keyCargas = order.find((t) =>
    isTituloCargasPersonal(t, map[t]?.codigoMin),
  );
  const keyProvisiones = order.find((t) =>
    isTituloProvisionesPersonal(t, map[t]?.codigoMin),
  );
  const gastosPersonalKeys = [keySueldos, keyCargas, keyProvisiones].filter(
    (k) => k && map[k],
  );
  if (gastosPersonalKeys.length > 0) {
    const GASTOS_PERSONAL_TITULO = "6. Gastos de personal.";
    const subTitles = [
      { key: keySueldos, label: "a) Sueldos, salarios y asimilados." },
      { key: keyCargas, label: "b) Cargas sociales." },
      { key: keyProvisiones, label: "c) Provisiones." },
    ].filter((s) => s.key && map[s.key]);
    const subGroupOrder = subTitles.map((s) => s.label);
    const subGroups = {};
    let totalMes = {};
    let totalSum = 0;
    subTitles.forEach(({ key, label }) => {
      const grp = map[key];
      subGroups[label] = {
        title: label,
        accountList: flattenAccountList(grp),
        mes: { ...grp.mes },
        total: grp.total,
      };
      for (let m = 1; m <= 12; m++) {
        totalMes[m] = (totalMes[m] || 0) + (grp.mes[m] || 0);
      }
      totalSum += grp.total;
    });
    map[GASTOS_PERSONAL_TITULO] = {
      titulo: GASTOS_PERSONAL_TITULO,
      codigoMin: "A.06",
      mes: totalMes,
      total: totalSum,
      subGroupOrder,
      subGroups,
      _virtualParent: true,
    };
    const minPos = Math.min(...gastosPersonalKeys.map((k) => order.indexOf(k)));
    const newOrder = order.filter((k) => !gastosPersonalKeys.includes(k));
    const spliceAt = newOrder.filter((k) => order.indexOf(k) < minPos).length;
    newOrder.splice(spliceAt, 0, GASTOS_PERSONAL_TITULO);
    order.length = 0;
    order.push(...newOrder);
  }

  NPGC_VIRTUAL_PARENTS.forEach((cfg) => applyVirtualParent(order, map, cfg));

  order.sort((a, b) => {
    const ca = map[a]?.codigoMin || "Z";
    const cb = map[b]?.codigoMin || "Z";
    return ca.localeCompare(cb, undefined, { numeric: true });
  });

  const calculatedValues = {};
  calculatedValues["A.1) RESULTADO DE EXPLOTACIÓN"] = sumGruposPorSeccion(
    order,
    map,
    isExplotacionSection,
  );
  calculatedValues["A.2) RESULTADO FINANCIERO"] = sumGruposPorSeccion(
    order,
    map,
    isFinancieroSection,
  );

  for (const cfg of CALCULATED_LINES) {
    if (cfg.kind === "explotacion" || cfg.kind === "financiero") continue;
    if (cfg.formulaLines) {
      const mes = {};
      let total = 0;
      for (let m = 1; m <= 12; m++) mes[m] = 0;
      for (const { line, sign } of cfg.formulaLines) {
        const v = calculatedValues[line];
        if (v) {
          for (let m = 1; m <= 12; m++) {
            mes[m] += (sign ?? 1) * (v.mes[m] || 0);
          }
          total += (sign ?? 1) * (v.total || 0);
        }
      }
      calculatedValues[cfg.key] = { mes, total };
    }
  }

  for (const cfg of CALCULATED_LINES) {
    const val = calculatedValues[cfg.key];
    if (!val) continue;
    order.push(cfg.key);
    map[cfg.key] = {
      titulo: cfg.key,
      mes: val.mes,
      total: val.total,
      subGroupOrder: [],
      subGroups: {},
      _calculated: true,
    };
  }

  const keyResultadoEjercicio = "A.5) RESULTADO DEL EJERCICIO";
  const idxA5 = order.indexOf(keyResultadoEjercicio);

  if (idxA5 !== -1 && map[keyResultadoEjercicio]) {
    EXTRA_BLOCK_TITLES.forEach((titulo) => {
      let origen = null;
      if (titulo === "PROYECCIÓN VENTAS") origen = resultadoProyeccionVentas;
      if (titulo === "PROYECCIÓN COMPRAS") origen = resultadoProyeccionCompras;
      if (titulo === "PEDIDOS VENTA") origen = resultadoPedidosVenta;
      if (titulo === "PEDIDOS COMPRA") origen = resultadoPedidosCompra;
      if (titulo === "FACTURAS BORRADOR VENTA") {
        origen = resultadoFacturasBorradorVenta;
      }

      const mes = {};
      let total = 0;
      for (let m = 1; m <= 12; m++) {
        const val = origen?.[m] ?? origen?.[String(m)] ?? 0;
        mes[m] = val;
        total += val;
      }

      map[titulo] = {
        titulo,
        mes,
        total,
        subGroupOrder: [],
        subGroups: {},
      };

      if (!order.includes(titulo)) {
        order.push(titulo);
      }
    });

    const existingExtraTitles = EXTRA_BLOCK_TITLES.filter((titulo) =>
      order.includes(titulo),
    );

    if (existingExtraTitles.length > 0) {
      existingExtraTitles.forEach((titulo) => {
        const idx = order.indexOf(titulo);
        if (idx !== -1) {
          order.splice(idx, 1);
        }
      });

      let insertPos = order.indexOf(keyResultadoEjercicio) + 1;
      order.splice(insertPos, 0, ...existingExtraTitles);
      insertPos += existingExtraTitles.length;

      const base = map[keyResultadoEjercicio];
      const resultadoFinalMes = {};
      let resultadoFinalTotal = 0;

      for (let m = 1; m <= 12; m++) {
        const claveMesNumero = m;
        const claveMesTexto = String(m);

        let valorMes =
          base.mes?.[claveMesNumero] ?? base.mes?.[claveMesTexto] ?? 0;

        existingExtraTitles.forEach((titulo) => {
          const grp = map[titulo];
          if (!grp || !grp.mes) return;
          valorMes += grp.mes[claveMesNumero] ?? grp.mes[claveMesTexto] ?? 0;
        });

        resultadoFinalMes[claveMesNumero] = valorMes;
        resultadoFinalTotal += valorMes;
      }

      const keyResultadoFinal = "RESULTADO FINAL";
      const idxExistingFinal = order.indexOf(keyResultadoFinal);
      if (idxExistingFinal !== -1) {
        order.splice(idxExistingFinal, 1);
      }

      order.splice(insertPos, 0, keyResultadoFinal);
      map[keyResultadoFinal] = {
        titulo: keyResultadoFinal,
        mes: resultadoFinalMes,
        total: resultadoFinalTotal,
        subGroupOrder: [],
        subGroups: {},
        _calculated: true,
      };
    }
  }

  return { groupOrder: order, groupMap: map };
};

export const buildExcelRows = (groupOrder, groupMap, meses = MESES, opts = {}) => {
  const translateTitle = opts.translateTitle || ((s) => s);
  const titleHeader = opts.titleHeader || "Título";
  const totalHeader = opts.totalHeader || "Total";
  const headers = [titleHeader, ...meses, totalHeader];
  const rows = [];
  const addRow = (titulo, mesData = {}, total = 0) => {
    const row = { [titleHeader]: translateTitle(titulo) };
    meses.forEach((mes) => {
      const mesNum = Number(mes);
      row[mes] = Number(mesData[mesNum] ?? mesData[mes] ?? 0);
    });
    row[totalHeader] = Number(total || 0);
    rows.push(row);
  };

  groupOrder.forEach((titulo) => {
    const g = groupMap[titulo];
    if (!g) return;
    addRow(g.titulo || titulo, g.mes, g.total);
    (g.subGroupOrder || []).forEach((subKey) => {
      const sub = g.subGroups[subKey];
      if (!sub) return;
      if (sub.title) addRow(`  ${sub.title}`, sub.mes, sub.total);
      (sub.accountList || []).forEach((acc) => {
        addRow(`    ${acc.titulo || acc.codigo}`, acc.mes, acc.total);
      });
    });
  });

  return { headers, rows };
};

const TITULO_VENTA_NPGC = PYG_TITULO_VENTA_NPGC;
const TITULO_COSTE_NPGC = "4. Aprovisionamientos.";
export const PYG_KEY_RESULTADO_FINAL = "RESULTADO FINAL";
const KEY_RESULTADO_FINAL = PYG_KEY_RESULTADO_FINAL;

const MARGEN_GRUPO_KEYS = new Set([
  TITULO_VENTA_NPGC,
  TITULO_COSTE_NPGC,
  ...EXTRA_BLOCK_TITLES,
]);

const isGrupoMargenComercial = (g, key) => {
  if (!g || g._calculated) return false;
  const titulo = (g.titulo || key || "").trim();
  if (MARGEN_GRUPO_KEYS.has(titulo) || MARGEN_GRUPO_KEYS.has(key)) return true;
  if (
    titulo.startsWith("2. Variación") ||
    titulo.startsWith("3. Trabajos realizados")
  ) {
    return true;
  }
  const section = getCodigoSection(g.codigoMin);
  return section === 2 || section === 3;
};

/** Margen mensual: cifra de negocios + coste producto + proyecciones y pedidos. */
export const calcMargenComercialMensual = (groupMap, groupOrder = []) => {
  const mes = {};
  for (let m = 1; m <= 12; m++) mes[m] = 0;

  groupOrder.forEach((key) => {
    const g = groupMap[key];
    if (!isGrupoMargenComercial(g, key)) return;
    for (let m = 1; m <= 12; m++) {
      const v = Number(g.mes?.[m] ?? g.mes?.[String(m)] ?? 0);
      mes[m] = (mes[m] || 0) + v;
    }
  });

  let total = 0;
  for (let m = 1; m <= 12; m++) total += mes[m] || 0;
  return { mes, total };
};

/** Filas de pipeline que suman en venta total (misma lógica que paretoMarcaUtils). */
export const VENTA_PIPELINE_TITLES = [
  PYG_TITULO_VENTA_NPGC,
  "PROYECCIÓN VENTAS",
  "PEDIDOS VENTA",
  "FACTURAS BORRADOR VENTA",
];

export const VENTA_PIPELINE_SERIES = [
  { key: "contable", title: PYG_TITULO_VENTA_NPGC, label: "Venta contable" },
  { key: "proyeccionVentas", title: "PROYECCIÓN VENTAS", label: "Proyección ventas" },
  { key: "pedidosVenta", title: "PEDIDOS VENTA", label: "Pedidos venta abiertos" },
  {
    key: "facturasBorrador",
    title: "FACTURAS BORRADOR VENTA",
    label: "Facturas borrador",
  },
];

const extractMesSerieFromGroup = (groupMap, titulo) => {
  const g = groupMap[titulo];
  const mes = {};
  for (let m = 1; m <= 12; m++) {
    mes[m] = Number(g?.mes?.[m] ?? g?.mes?.[String(m)] ?? 0);
  }
  return { mes, total: Number(g?.total ?? 0) };
};

/** Epígrafe 1 — cifra de negocios (venta facturada NPGC, sin pipeline). */
export const calcVentaMensual = (groupMap) =>
  extractMesSerieFromGroup(groupMap, PYG_TITULO_VENTA_NPGC);

/** Venta total = contable + proyección ventas + pedidos venta + facturas borrador. */
export const calcVentaTotalMensual = (groupMap) => {
  const mes = {};
  for (let m = 1; m <= 12; m++) mes[m] = 0;

  VENTA_PIPELINE_TITLES.forEach((titulo) => {
    const { mes: src } = extractMesSerieFromGroup(groupMap, titulo);
    for (let m = 1; m <= 12; m++) {
      mes[m] += src[m] || 0;
    }
  });

  let total = 0;
  for (let m = 1; m <= 12; m++) total += mes[m] || 0;
  return { mes, total };
};

/** Desglose mensual del pipeline de ventas (para gráficos apilados). */
export const calcVentaPipelineBreakdownMensual = (groupMap) => {
  const breakdown = {};
  VENTA_PIPELINE_SERIES.forEach(({ key, title }) => {
    breakdown[key] = extractMesSerieFromGroup(groupMap, title);
  });
  return breakdown;
};

/** Misma fila RESULTADO FINAL que la tabla (A.5 + proyecciones y pedidos). */
export const calcResultadoFinalMensual = (groupMap) => {
  const g = groupMap[PYG_KEY_RESULTADO_FINAL];
  const mes = {};
  for (let m = 1; m <= 12; m++) {
    mes[m] = Number(g?.mes?.[m] ?? g?.mes?.[String(m)] ?? 0);
  }
  const total = Number(g?.total ?? 0);
  return { mes, total };
};

const filterSerieRowsForMarca = (rows, marca, labelFields) =>
  (rows || []).filter((row) => {
    for (const field of labelFields) {
      const v = row?.[field];
      if (v != null && String(v).trim() && matchMarcaFromText(v, [marca]) === marca) {
        return true;
      }
    }
    return false;
  });

/**
 * KPI cabecera acordeón: mismos totales que la tabla expandida
 * (epígrafes 1 y 4 + RESULTADO FINAL = A.5 + proyecciones + pedidos).
 */
export const computeMarcasKpi = (
  marcas,
  {
    saldosRows = [],
    proyeccionVentas = [],
    proyeccionCompras = [],
    pedidosVenta = [],
    pedidosCompra = [],
    facturasBorradorVenta = [],
  },
) => {
  const byMarca = {};

  marcas.forEach((marca) => {
    const saldosMarca = (saldosRows || []).filter(
      (r) => String(r.Nivel2Analitica ?? r.nivel2Analitica ?? "").trim() === marca,
    );

    const pv = filterSerieRowsForMarca(proyeccionVentas, marca, ["nomcli", "NOMCLI"]);
    const pc = filterSerieRowsForMarca(proyeccionCompras, marca, ["nompro", "NOMPRO"]);
    const pven = filterSerieRowsForMarca(pedidosVenta, marca, ["cliente", "CLIPRO"]);
    const pcom = filterSerieRowsForMarca(pedidosCompra, marca, ["proveedor", "CLIPRO"]);
    const fbv = filterSerieRowsForMarca(facturasBorradorVenta, marca, [
      "cliente",
      "CLIPRO",
    ]);

    const { groupMap } = buildPygAnaliticoStructure(saldosMarca, {
      resultadoProyeccionVentas: calcularProyeccionPorMes(pv),
      resultadoProyeccionCompras: calcularProyeccionPorMes(pc),
      resultadoPedidosVenta: calcularProyeccionPorMes(pven),
      resultadoPedidosCompra: calcularProyeccionPorMes(pcom),
      resultadoFacturasBorradorVenta: calcularProyeccionPorMes(fbv),
    });

    byMarca[marca] = {
      venta: groupMap[TITULO_VENTA_NPGC]?.total ?? 0,
      coste: groupMap[TITULO_COSTE_NPGC]?.total ?? 0,
      proyeccionVentas: groupMap["PROYECCIÓN VENTAS"]?.total ?? 0,
      proyeccionCompras: groupMap["PROYECCIÓN COMPRAS"]?.total ?? 0,
      pedidosVenta: groupMap["PEDIDOS VENTA"]?.total ?? 0,
      pedidosCompra: groupMap["PEDIDOS COMPRA"]?.total ?? 0,
      facturasBorradorVenta: groupMap["FACTURAS BORRADOR VENTA"]?.total ?? 0,
      resultado: groupMap[KEY_RESULTADO_FINAL]?.total ?? 0,
    };
  });

  return byMarca;
};

/** @deprecated Use computeMarcasKpi */
export const aggregateMarcasKpi = (rawRows) => {
  const byMarca = aggregateMarcasKpiSaldos(rawRows);
  Object.keys(byMarca).forEach((marca) => {
    const k = byMarca[marca];
    k.resultado = k.venta + k.coste;
  });
  return byMarca;
};
